import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { MagicReportSection } from './MagicReportSection';
import { CommentsSection } from './CommentsSection';
import { DeclarativeLayoutRenderer } from './DeclarativeLayoutRenderer';
import { REALLOCATIONS_SECTION_CONFIG } from './sections.config';
import { DrawContext, SectionFieldCollection, SectionFieldDTO, FieldType } from '../magic-report.type';
import { getCommentsField } from './section-utils';
import { ReallocationsTable, ReallocationRow } from './ReallocationsTable';
import { useMagicReport } from '../context/MagicReportContext';

const ROWS_FIELD = 'ReallocationsRows';
const COMMENT_1_FIELD = 'ReallocationsComment1';
const COMMENT_2_FIELD = 'ReallocationsComment2';

const generateComment1 = (currentPeriodRows: ReallocationRow[], allRows: ReallocationRow[] = currentPeriodRows): string => {
  if (!currentPeriodRows.length) return '';

  // Calculate reallocation change this period (sum of current reallocation, current-period rows only)
  const reallocationChangeThisPeriod = currentPeriodRows.reduce((sum, row) => sum + row.currentReallocation, 0);

  // Calculate total reallocation amount to date (sum of total reallocation across ALL rows,
  // including ones folded into the historical baseline - this must reflect the full cumulative
  // amount, not just what's visible this period).
  const totalReallocationToDate = allRows.reduce((sum, row) => sum + row.totalReallocation, 0);

  const periodText =
    reallocationChangeThisPeriod === 0
      ? 'no net effect'
      : reallocationChangeThisPeriod > 0
        ? `$${Math.abs(reallocationChangeThisPeriod).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} increase`
        : `$${Math.abs(reallocationChangeThisPeriod).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} decrease`;

  const overallText =
    totalReallocationToDate === 0
      ? 'no net change'
      : totalReallocationToDate > 0
        ? `$${Math.abs(totalReallocationToDate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} increase`
        : `$${Math.abs(totalReallocationToDate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} decrease`;

  return `The current reallocations have resulted in ${periodText} on the hard cost budget this period. Overall there has been a ${overallText} in the original hard cost schedule of values.`;
};

const COMMENT_2_DEFAULT_TEXT =
  'It is our understanding that the contractor or borrower is reallocating funds as subcontracts are executed in order to maintain a properly allocated Schedule of Values. Our review of the reallocations reveal that the contractor is not "Front End Loading." (Front End Loading is defined as the Contractor\'s disproportionate allocation of budgets in the Schedule of Values to the early stages of the construction, allowing them to receive funds greater than required at the beginning of the project and creating the risk that insufficient funds would be available to complete construction at the end of the project, if the Contractor were to default).';

const ReallocationsSectionContent: React.FC<{
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  onBook?: () => number;
}> = ({ data, handleFieldChange, getField, onBook }) => {
  const { reportState, registerSectionSync, unregisterSectionSync } = useMagicReport();
  // Only the first open of a period fills from the budget; after that, Get Reallocations does.
  const autoSync = Boolean(reportState.initialSyncPending);

  // Every covered draw (G702), oldest application first. Reports opened before multi-draw have no
  // list, so the single primary-draw mirror becomes a one-draw list and this renders as before.
  const draws: DrawContext[] = useMemo(() => {
    const fromState = reportState.draws as DrawContext[] | undefined;
    if (fromState?.length) return fromState;
    if (!reportState.drawSummary) return [];
    return [
      {
        drawId: reportState.drawSummary.id,
        drawSummary: reportState.drawSummary,
        budgetSummary: reportState.budgetSummary,
        lineItems: reportState.lineItems,
      },
    ];
  }, [reportState.draws, reportState.drawSummary, reportState.budgetSummary, reportState.lineItems]);

  const firstDrawId = draws[0]?.drawId;
  // Comment generation spans every G702, so it works off all draws' line items together.
  const lineItems = useMemo(() => draws.flatMap(d => d.lineItems || []), [draws]);

  const rowsField = getField(ROWS_FIELD);

  /** Builds one draw's rows from its own reallocated line items. */
  const buildRowsForDraw = useCallback((draw: DrawContext): ReallocationRow[] => {
    const reallocatedItems = (draw.lineItems || []).filter(li => {
      if (li.isDeleted) return false;
      return (li.reallocation || 0) !== 0;
    });

    return reallocatedItems.map(li => {
      const originalBudget = li.originalBudget || 0;
      const totalReallocation = li.reallocation || 0;
      const currentReallocation = totalReallocation;
      return {
        lineItemId: li.id,
        drawId: draw.drawId,
        description: li.lineItem?.description || '',
        originalBudget,
        totalReallocation,
        percentChange: originalBudget !== 0 ? (currentReallocation / originalBudget) * 100 : 0,
        revisedBudget: originalBudget + totalReallocation,
        currentReallocation,
        isLastMonth: false,
      };
    });
  }, []);

  const allRows: ReallocationRow[] = useMemo(() => {
    // First, try to read saved rows. Rows saved before multi-draw carry no `drawId` and are bound
    // to the first covered draw so they keep showing up in its table.
    const savedRows =
      rowsField?.entries
        ?.map(entry => {
          try {
            return JSON.parse(entry.value || '') as ReallocationRow;
          } catch {
            return null;
          }
        })
        .filter((r): r is ReallocationRow => r !== null)
        .map(r => (r.drawId ? r : { ...r, drawId: firstDrawId })) || [];

    if (savedRows.length > 0) {
      // Seed any draw that has reallocated line items but no saved rows yet.
      const drawsWithoutRows = draws.filter(d => !savedRows.some(r => r.drawId === d.drawId));
      return autoSync ? [...savedRows, ...drawsWithoutRows.flatMap(buildRowsForDraw)] : savedRows;
    }

    // If no saved rows, build from line items (so comments/table bootstrap).
    // Since there is no prior saved state yet, every reallocated line item's entire
    // cumulative amount is new this period - there is nothing to fold into a baseline.
    return autoSync ? draws.flatMap(buildRowsForDraw) : [];
  }, [rowsField, draws, firstDrawId, buildRowsForDraw, autoSync]);

  const rowsByDraw = useMemo(() => {
    const grouped = new Map<string, ReallocationRow[]>();
    draws.forEach(d => grouped.set(d.drawId, []));
    allRows.forEach(row => {
      const key = row.drawId || firstDrawId;
      if (!key) return;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(row);
    });
    return grouped;
  }, [allRows, draws, firstDrawId]);

  /**
   * Authoritative row list for merging writes.
   *
   * Every draw's table writes to the same `ReallocationsRows` field and several are mounted at
   * once, so merging against a render-time snapshot would let two writes in the same tick clobber
   * each other. This ref is updated synchronously at the merge point below, so the second write in
   * a tick sees the first one's result.
   */
  const allRowsRef = React.useRef<ReallocationRow[]>(allRows);
  allRowsRef.current = allRows;

  /**
   * Single write point for the shared rows field: serializes every draw's rows and advances the
   * ref synchronously so a second write in the same tick sees this one's result. `updates` carries
   * anything else the caller was setting on the field (a bookmark toggle, say) through untouched.
   */
  const writeAllRows = useCallback(
    (nextRows: ReallocationRow[], updates?: Partial<SectionFieldDTO>) => {
      allRowsRef.current = nextRows;
      handleFieldChange(ROWS_FIELD, {
        fieldType: FieldType.TextArray,
        ...updates,
        entries: nextRows.map((row, index) => ({ value: JSON.stringify(row), order: index })),
      });
    },
    [handleFieldChange],
  );

  /**
   * A `handleFieldChange` scoped to one draw's table: writes to the shared rows field replace only
   * that draw's rows and leave every other draw's untouched. Anything else passes straight through,
   * so ReallocationsTable needs no changes — it still thinks it owns the field.
   */
  const makeDrawScopedFieldChange = useCallback(
    (drawId: string) => (fieldName: string, updates: Partial<SectionFieldDTO>) => {
      if (fieldName !== ROWS_FIELD || !updates.entries) {
        handleFieldChange(fieldName, updates);
        return;
      }
      const mine = updates.entries
        .map((entry): ReallocationRow | null => {
          try {
            return { ...(JSON.parse(entry.value || '') as ReallocationRow), drawId };
          } catch {
            return null;
          }
        })
        .filter((r): r is ReallocationRow => r !== null);
      const others = allRowsRef.current.filter(r => (r.drawId || firstDrawId) !== drawId);
      writeAllRows([...others, ...mine], updates);
    },
    [firstDrawId, handleFieldChange, writeAllRows],
  );

  /**
   * Persists the budget-derived seed rows.
   *
   * Each table is handed a synthetic rows field holding only its own draw's rows (see
   * `scopedRowsField` below), so ReallocationsTable's own "no saved rows -> persist" effect never
   * sees an empty field and never fires. Without this the seeded rows would live only in the
   * `allRows` memo: they render, but `ReallocationsRows.entries` stays empty on save, so the reset
   * has nothing to mark as last period's and this period's reallocations can't be told apart from
   * the historical baseline.
   *
   * Runs once per mount. The ref is also set when saved rows are already present, so a row whose
   * Current Reallocation was zeroed out can't be mistaken for "never seeded" and re-seeded.
   */
  const hasSeededRowsRef = React.useRef(false);
  useEffect(() => {
    if (hasSeededRowsRef.current) return;
    if ((rowsField?.entries?.length ?? 0) > 0) {
      hasSeededRowsRef.current = true;
      return;
    }
    if (allRows.length === 0) return;
    hasSeededRowsRef.current = true;
    writeAllRows(allRows);
  }, [rowsField, allRows, writeAllRows]);

  // Only rows with a nonzero Current Reallocation this period drive comment generation and
  // field visibility - a row folded into the historical baseline (isLastMonth, or currentReallocation
  // reset to 0) has nothing new to report.
  const rows: ReallocationRow[] = useMemo(() => allRows.filter(r => !r.isLastMonth && r.currentReallocation !== 0), [allRows]);

  const commentsField = getCommentsField(data, {
    isCommentField: (name, type) => type === FieldType.TextArray && name.toLowerCase().includes('comment'),
  });

  // Tracks the last auto-generated Comment 1 text so we can tell a stale auto-comment (which
  // should keep following the table) apart from a genuine manual edit (which must be preserved).
  const lastAutoComment1Ref = React.useRef<string | null>(null);
  // Tracks whether the user deliberately emptied Comment 1, so an empty value isn't mistaken
  // for "never generated yet" and immediately overwritten with a fresh auto-comment.
  const wasManuallyClearedRef = React.useRef(false);
  // Same as above, but for Comment 2 - without this, clearing Comment 2's text got immediately
  // overwritten with COMMENT_2_DEFAULT_TEXT the next time this effect ran for any reason.
  const wasComment2ManuallyClearedRef = React.useRef(false);
  // Tracks the row signature comments were generated for, so a genuine table change (not the
  // clearing itself) is what lifts the manual-clear flags and lets auto-generation resume.
  const lastRowsSignatureRef = React.useRef<string | null>(null);

  useEffect(() => {
    const rowsSignature = JSON.stringify(rows);
    if (lastRowsSignatureRef.current !== null && lastRowsSignatureRef.current !== rowsSignature) {
      wasManuallyClearedRef.current = false;
      wasComment2ManuallyClearedRef.current = false;
    }
    lastRowsSignatureRef.current = rowsSignature;

    if (rows.length > 0) {
      const currentComment1 = (getField(COMMENT_1_FIELD)?.value || '').trim();
      const autoComment1 = generateComment1(rows, allRows);

      if (!currentComment1) {
        wasManuallyClearedRef.current = true;
      }

      // Regenerate whenever the field is empty, or still holds the previous auto-generated text
      // (i.e. it hasn't been manually edited since) - this keeps the comment in sync as the table
      // changes (e.g. line items edited elsewhere) without clobbering manual edits or a deliberate clear.
      const isStaleAutoComment = currentComment1 === (lastAutoComment1Ref.current || '').trim();
      if (!wasManuallyClearedRef.current && (!currentComment1 || isStaleAutoComment) && autoComment1 && currentComment1 !== autoComment1) {
        handleFieldChange(COMMENT_1_FIELD, { value: autoComment1 });
      }
      lastAutoComment1Ref.current = autoComment1;

      const currentComment2 = (getField(COMMENT_2_FIELD)?.value || '').trim();
      if (!currentComment2) {
        wasComment2ManuallyClearedRef.current = true;
      }
      // Only auto-populate when the field is empty AND it hasn't been deliberately cleared since
      // the table last changed - otherwise clearing it gets immediately overwritten.
      if (!wasComment2ManuallyClearedRef.current && !currentComment2) {
        handleFieldChange(COMMENT_2_FIELD, { value: COMMENT_2_DEFAULT_TEXT });
      }
    } else {
      // Clear comments if no rows. Skip ones already empty: this runs on every load, and a write
      // marks the report dirty before the reviewer has touched anything.
      if (getField(COMMENT_1_FIELD)?.value) handleFieldChange(COMMENT_1_FIELD, { value: '' });
      if (getField(COMMENT_2_FIELD)?.value) handleFieldChange(COMMENT_2_FIELD, { value: '' });
      lastAutoComment1Ref.current = null;
      wasManuallyClearedRef.current = false;
      wasComment2ManuallyClearedRef.current = false;
    }
  }, [rows, allRows, getField, handleFieldChange]);

  const comment2Field = getField(COMMENT_2_FIELD);

  const handleRefreshComment1 = useCallback(() => {
    const autoComment1 = generateComment1(rows, allRows);
    lastAutoComment1Ref.current = autoComment1;
    wasManuallyClearedRef.current = false;
    handleFieldChange(COMMENT_1_FIELD, { value: autoComment1 });
  }, [rows, allRows, handleFieldChange]);

  const handleRefreshComment2 = useCallback(() => {
    wasComment2ManuallyClearedRef.current = false;
    handleFieldChange(COMMENT_2_FIELD, { value: COMMENT_2_DEFAULT_TEXT });
  }, [handleFieldChange]);

  // One sync function per draw's table; "Run this Month" runs them all.
  const syncFnsRef = React.useRef<Map<string, () => Promise<void>>>(new Map());
  useEffect(() => {
    const syncId = 'reallocations';
    registerSectionSync(syncId, async () => {
      await Promise.all(Array.from(syncFnsRef.current.values()).map(fn => fn()));
    });
    return () => {
      unregisterSectionSync(syncId);
    };
  }, [registerSectionSync, unregisterSectionSync]);

  const layout = useMemo(
    () => ({
      singleColumn: [
        // The table is split by G702: one labelled table per covered draw, each with its own
        // "Total Change Amounts" footer. The comments below stay section-level and are generated
        // from every draw's rows together.
        ...draws.map((draw, drawIndex) => {
          const drawId = draw.drawId;
          const drawRows = rowsByDraw.get(drawId) || [];
          const scopedFieldChange = makeDrawScopedFieldChange(drawId);
          // The table reads its rows from this field, so hand it only this draw's entries.
          const scopedRowsField: SectionFieldDTO | undefined = rowsField
            ? { ...rowsField, entries: drawRows.map((row, index) => ({ value: JSON.stringify(row), order: index })) }
            : undefined;
          const label = draw.drawSummary?.name || `Draw ${drawIndex + 1}`;

          return {
            fieldName: `${ROWS_FIELD}__${drawId}`,
            type: 'custom-component' as const,
            label: '',
            renderComponent: () => (
              <Box sx={{ mt: drawIndex === 0 ? 0 : 3 }}>
                <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                  {label} Reallocated Line Items:
                </Typography>
                <ReallocationsTable
                  autoSync={autoSync}
                  rowsField={scopedRowsField}
                  handleFieldChange={scopedFieldChange}
                  lineItems={draw.lineItems}
                  onRowsSynced={updated => {
                    // Regenerate the single comment from this draw's synced rows plus every other
                    // draw's current rows, so its sums span all G702s.
                    const others = allRowsRef.current.filter(r => (r.drawId || firstDrawId) !== drawId);
                    const combined = [...others, ...updated.map(r => ({ ...r, drawId }))];
                    const currentMonthRows = combined.filter(r => !r.isLastMonth && r.currentReallocation !== 0);
                    const autoComment1 = generateComment1(currentMonthRows, combined);
                    lastAutoComment1Ref.current = autoComment1;
                    handleFieldChange(COMMENT_1_FIELD, { value: autoComment1 });
                    if (currentMonthRows.length > 0 && !comment2Field?.value) {
                      wasComment2ManuallyClearedRef.current = false;
                      handleFieldChange(COMMENT_2_FIELD, { value: COMMENT_2_DEFAULT_TEXT });
                    }
                  }}
                  onSyncFunctionReady={syncFn => {
                    syncFnsRef.current.set(drawId, syncFn);
                  }}
                />
              </Box>
            ),
          };
        }),
        ...(rows.length > 0
          ? [
              {
                fieldName: COMMENT_1_FIELD,
                type: 'text-with-refresh' as const,
                label: '',
                onRefresh: handleRefreshComment1,
              },
              {
                fieldName: COMMENT_2_FIELD,
                type: 'text-with-refresh' as const,
                label: '',
                onRefresh: handleRefreshComment2,
              },
            ]
          : []),
      ],
    }),
    [
      draws,
      rowsField,
      rowsByDraw,
      makeDrawScopedFieldChange,
      firstDrawId,
      handleFieldChange,
      rows,
      comment2Field,
      handleRefreshComment1,
      handleRefreshComment2,
      autoSync,
    ],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <DeclarativeLayoutRenderer layout={layout} getField={getField} handleFieldChange={handleFieldChange} onBook={onBook} />
      {commentsField && (
        <CommentsSection
          commentsField={commentsField}
          onChange={updated => handleFieldChange(commentsField.fieldName, updated)}
          onBook={onBook}
        />
      )}
    </Box>
  );
};

export const ReallocationsSection: React.FC<{ magicReportId: string }> = ({ magicReportId }) => {
  return (
    <MagicReportSection
      magicReportId={magicReportId}
      sectionConfig={REALLOCATIONS_SECTION_CONFIG}
      customization={{
        renderContent: (data, handleFieldChange, getField, onBook) => (
          <ReallocationsSectionContent data={data} handleFieldChange={handleFieldChange} getField={getField} onBook={onBook} />
        ),
      }}
    />
  );
};
